import { useState } from 'react';
import {
  useDocuments,
  useUploadDocument,
  useDeleteDocument,
  useDocumentVersions,
  useUploadDocumentVersion,
  type DocumentType,
  type DocumentRow,
} from '../api/queries/documents';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { fmtDateTime } from '../utils/date';
import { LegalDocumentGenerator } from './LegalDocumentGenerator';
import { EngagementLetterIngest } from './ingest/EngagementLetterIngest';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { LookupSelect } from './LookupSelect';
import { useLookupLabel } from '../api/queries/lookups';
import './ProjectDocuments.css';

const MAX_BYTES = 50 * 1024 * 1024;

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface ProjectDocumentsProps {
  projectId: number;
  projectCaseNumber: string | null;
}

export function ProjectDocuments({
  projectId,
  projectCaseNumber,
}: ProjectDocumentsProps): JSX.Element {
  // Doubles as the list filter AND the type new uploads are tagged with.
  const [uploadType, setUploadType] = useState<DocumentType>('engagement_letter');
  const [filter, setFilter] = useState<DocumentType | ''>('');
  const typeLabel = useLookupLabel('document_type');
  const { data, isPending, error } = useDocuments({
    project_id: projectId,
    ...(filter ? { document_type: filter } : {}),
  });
  const upload = useUploadDocument();
  const del = useDeleteDocument();
  const canWrite = can('document.write');
  const canDelete = can('document.delete');
  const [actionError, setActionError] = useState<string | null>(null);
  const [versionsForId, setVersionsForId] = useState<number | null>(null);
  const [previewDoc, setPreviewDoc] = useState<DocumentRow | null>(null);

  const onPickUpload = async (file: File) => {
    if (file.size > MAX_BYTES) {
      setActionError('Files must be under 50 MB.');
      return;
    }
    setActionError(null);
    try {
      await upload.mutateAsync({ file, document_type: uploadType, project_id: projectId });
    } catch (err) {
      setActionError(err instanceof ApiError ? `Upload failed (${err.status}).` : 'Upload failed.');
    }
  };

  const onDelete = (doc: DocumentRow) => {
    if (!confirm(`Delete "${doc.original_filename}"? The file is removed from the vault.`)) return;
    const reason = prompt('Reason for deletion (recorded in the audit log):', '');
    if (reason === null) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0 &&
        !confirm('No reason given — proceed without one? An audit entry will still be written.')) {
      return;
    }
    del.mutate(
      { id: doc.id, reason: trimmed.length > 0 ? trimmed : undefined },
      {
        onError: (err) =>
          setActionError(
            err instanceof ApiError && err.status === 403
              ? "You don't have permission to delete documents."
              : 'Could not delete document.',
          ),
      },
    );
  };

  const docs = data?.documents ?? [];

  return (
    <section className="lgd-block pdoc-block">
      <header className="lgd-header">
        <h3>Documents</h3>
        <span className="lgd-meta">
          Uploaded & generated files for this case — stored encrypted in the case folder
        </span>
      </header>

      <div className="pdoc-toolbar">
        <label className="pdoc-inline">
          <span>Show</span>
          <LookupSelect
            category="document_type"
            value={filter}
            onChange={setFilter}
            className="form-input pdoc-select"
            allowEmpty
            emptyLabel="All types"
          />
        </label>

        {canWrite && (
          <div className="pdoc-upload">
            <label className="pdoc-inline">
              <span>Upload as</span>
              <LookupSelect
                category="document_type"
                value={uploadType}
                onChange={setUploadType}
                className="form-input pdoc-select"
                disabled={upload.isPending}
              />
            </label>
            <label className="btn-cyan-primary pdoc-upload-btn">
              {upload.isPending ? 'Uploading…' : 'Upload file'}
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void onPickUpload(f);
                }}
                disabled={upload.isPending}
              />
            </label>
          </div>
        )}
      </div>

      {actionError && <p className="lgd-error" role="alert">{actionError}</p>}

      {isPending ? (
        <p className="pdoc-status">Loading documents…</p>
      ) : error ? (
        <p className="pdoc-status pdoc-error">
          {error instanceof ApiError && error.status === 403
            ? "You don't have permission to view documents."
            : 'Failed to load documents.'}
        </p>
      ) : docs.length === 0 ? (
        <p className="pdoc-status">
          No documents on file yet{canWrite ? ' — upload one above, or generate a letter below.' : '.'}
        </p>
      ) : (
        <table className="pdoc-table">
          <thead>
            <tr>
              <th>Filename</th>
              <th>Type</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th>By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} className={d.is_confidential ? 'pdoc-row-confidential' : ''}>
                <td className="pdoc-cell-name">
                  <button
                    type="button"
                    className="lgd-link"
                    onClick={() => setPreviewDoc(d)}
                    title="Preview"
                  >
                    {d.original_filename}
                  </button>
                  {d.is_confidential && <span className="pdoc-pill">Confidential</span>}
                </td>
                <td>{typeLabel(d.document_type)}</td>
                <td>{fmtBytes(d.file_size)}</td>
                <td>{fmtDateTime(d.created_at)}</td>
                <td>{d.created_by_name ?? '—'}</td>
                <td className="pdoc-actions">
                  <button
                    type="button"
                    className="lgd-link"
                    onClick={() => setVersionsForId(d.id)}
                    title="View version history"
                  >
                    versions{d.version_number > 1 ? ` (v${d.version_number})` : ''}
                  </button>
                  {canDelete && (
                    <button
                      type="button"
                      className="pdoc-delete"
                      onClick={() => onDelete(d)}
                      disabled={del.isPending}
                      aria-label="Delete"
                      title="Delete"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {can('document.generate') && (
        <>
          <div className="pdoc-divider" />
          <LegalDocumentGenerator projectId={projectId} projectCaseNumber={projectCaseNumber} />
        </>
      )}

      {can('document.generate') && can('document.write') && (
        <>
          <div className="pdoc-divider" />
          <section className="lgd-block">
            <header className="lgd-header">
              <h3>Ingest a Signed Letter</h3>
              <span className="lgd-meta">
                Drop a signed engagement letter PDF — we extract its details into this case
              </span>
            </header>
            <EngagementLetterIngest
              mode={{ kind: 'existing_project', projectId }}
              onApplied={() => setActionError(null)}
            />
          </section>
        </>
      )}

      {versionsForId !== null && (
        <VersionsModal
          docId={versionsForId}
          canWrite={canWrite}
          onClose={() => setVersionsForId(null)}
          onPreview={setPreviewDoc}
        />
      )}

      {previewDoc && (
        <DocumentPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </section>
  );
}

function VersionsModal({
  docId,
  canWrite,
  onClose,
  onPreview,
}: {
  docId: number;
  canWrite: boolean;
  onClose: () => void;
  onPreview: (doc: DocumentRow) => void;
}): JSX.Element {
  const versions = useDocumentVersions(docId);
  const newVersion = useUploadDocumentVersion();
  const [error, setError] = useState<string | null>(null);

  const onPickNewVersion = async (file: File) => {
    if (file.size > MAX_BYTES) {
      setError('Files must be under 50 MB.');
      return;
    }
    setError(null);
    try {
      await newVersion.mutateAsync({ parent_id: docId, file });
      void versions.refetch();
    } catch (err) {
      setError(err instanceof ApiError ? `Upload failed (${err.status}).` : 'Upload failed.');
    }
  };

  const rows = versions.data?.versions ?? [];
  return (
    <div className="pdoc-modal-backdrop" onClick={onClose}>
      <div className="pdoc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="pdoc-modal-header">
          <h3>Version History</h3>
          <button type="button" className="lgd-link" onClick={onClose}>Close</button>
        </header>
        {versions.isPending ? (
          <p className="pdoc-status">Loading…</p>
        ) : versions.error ? (
          <p className="pdoc-status pdoc-error">Could not load versions.</p>
        ) : rows.length === 0 ? (
          <p className="pdoc-status">No versions on file.</p>
        ) : (
          <table className="pdoc-table">
            <thead>
              <tr><th>v#</th><th>Filename</th><th>Size</th><th>Uploaded</th><th>By</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id}>
                  <td>v{v.version_number}</td>
                  <td>{v.original_filename}</td>
                  <td>{fmtBytes(v.file_size)}</td>
                  <td>{fmtDateTime(v.created_at)}</td>
                  <td>{v.created_by_name ?? '—'}</td>
                  <td>
                    <button type="button" className="lgd-link" onClick={() => onPreview(v)}>
                      preview
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canWrite && (
          <div className="pdoc-modal-actions">
            <label className="btn-cyan-primary pdoc-upload-btn">
              {newVersion.isPending ? 'Uploading…' : 'Upload new version'}
              <input
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void onPickNewVersion(f);
                }}
                disabled={newVersion.isPending}
              />
            </label>
            {error && <span className="field-error">{error}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
